'use client';

import { type FlexProps } from '@ironhack/design-system2/components/flex';
import { lineHeightForSize } from '@ironhack/design-system2/utils';
import { type ReactElement, useEffect } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { useSessionStorage } from 'react-use';

import {
  Storytelling,
  type StorytellingCard,
} from '@/app/components/client/storytelling';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import {
  type DatoBlock,
  type DatoButtonBlock,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms';
import { type WithTracking } from '@/types';
import { type PdpParamsParsed } from '../../page';

export type Header = DatoBlock<{
  button: [DatoButtonBlock];
  description: StructuredTextGraphQlResponse;
  image: DatoImage;
  tag: string;
  title: string;
}>;

type Props = { data: Header; params: PdpParamsParsed } & FlexProps;

export const Header = (props: Props): ReactElement | null => {
  const { data, params, ...flexProps } = props;
  const {
    button: [button],
    description,
    image,
    tag,
    title,
  } = data;
  const { campus: campusCode } = params;
  const [campus, setCampus] = useSessionStorage('campus', campusCode);

  useEffect(() => {
    if (campus !== campusCode) {
      setCampus(campusCode);
    }
  }, [campus, campusCode, setCampus]);

  const { isEnabled } = useFeatureToggle();
  if (isEnabled('pdp-hero-form')) return null;

  const storytellingData = {
    columns: [
      {
        button: [{ ...button, tracking: { f: 'pd' } }] as [
          WithTracking<DatoButtonBlock>
        ],
        card: [{ content: description }] as [StorytellingCard],
      },
      { image },
    ],
    headingProps: {
      as: 'h1' as const,
      lineHeight: [lineHeightForSize('3xl'), null, lineHeightForSize('6xl')],
      noOfLines: [3, null, 2],
      textStyle: ['3xl', null, '6xl'],
    },
    moduleFormat: [
      {
        background: 'transparent',
        spacingBottom: ['32', null, '80'],
        spacingTop: ['32', null, '40'],
      },
    ] as [DatoModuleFormat],
    tag,
    title,
  };

  return <Storytelling as="header" data={storytellingData} {...flexProps} />;
};
